import { JiraSideNavMessage } from '@post-office/components--jira-side-nav-message';
import { useMessageContext } from '@post-office/message-context';
import { useMessageInstanceEvent } from '@atlassian/post-office-context/message-lifecycle';

import { Body, productNameByVariant } from './Body';
import type { AdditionalProps, HydrationResult, TargetProduct } from '../types';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useRequestAccess } from '../useRequestAccess';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { FormattedMessage } from 'react-intl';

export type Props = HydrationResult &
	AdditionalProps & {
		targetProduct: TargetProduct;
	};

const whyAmISeeingThisReasons = [
	<FormattedMessage
		id="puma.common.cjn-nav4.reasons-modal.team-size"
		description="Reason explaining that the recommendation is based on the size of the user's team"
		defaultMessage="the size of your team"
	/>,
	<FormattedMessage
		id="puma.common.cjn-nav4.reasons-modal.existing-products"
		description="Reason explaining that the recommendation is based on the user's existing Atlassian products"
		defaultMessage="your existing Atlassian products"
	/>,
];

export const JiraSideNavCrossJoinMessage = ({
	targetProduct,
	collaborators,
	onDismissed,
	tenantId,
	userId,
	sourceProduct,
	crossJoinUrl,
}: Props): ReactNode => {
	const isFirstRender = useRef<boolean>(true);
	const [isVisible, setIsVisible] = useState(true);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceViewed } = useMessageInstanceEvent();

	const [isRequestSentFlagVisible, setIsRequestSentFlagVisible] = useState(false);
	const { messageTemplateId } = useMessageContext();
	const requestAccess = useRequestAccess({
		targetProduct,
		tenantId,
		userId,
		sourceProduct,
		collaborators,
		crossJoinUrl,
		messageTemplateId,
		onComplete: () => setIsVisible(false),
		showRequestSentFlag: () => setIsRequestSentFlagVisible(true),
	});

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			void messageInstanceViewed();
			void fireAnalyticsEvent({
				name: 'cjnNav4 placement',
				eventType: 'screen',
				attributes: {
					messageTemplateId,
					collaboratorCount: collaborators.length,
				},
			});
		}
	}, []);

	const attributes = useMemo(
		() => ({
			...(messageTemplateId ? { messageTemplateId } : {}),
		}),
		[messageTemplateId],
	);

	if (!isVisible) {
		return null;
	}

	return (
		<JiraSideNavMessage
			onDismissed={onDismissed}
			additionalAnalyticsAttributes={attributes}
			bodyContent={
				<Body
					sourceProduct={sourceProduct}
					targetProduct={targetProduct}
					collaborators={collaborators}
					tenantId={tenantId}
					userId={userId}
					crossJoinUrl={crossJoinUrl}
					messageTemplateId={messageTemplateId}
					onRequestAccess={requestAccess}
					isRequestSentFlagVisible={isRequestSentFlagVisible}
					setIsRequestSentFlagVisible={setIsRequestSentFlagVisible}
				/>
			}
			whyAmISeeingThisModalContent={{
				message: (
					<p>
						<FormattedMessage
							id="puma.common.cjn-nav4.reasons-modal.message"
							description="Explanation of why the recommended product may improve the user's team's productivity"
							defaultMessage="We think that {productName} will improve your team's productivity. This recommendation is based on:"
							values={{
								productName: <strong>{productNameByVariant[targetProduct]}</strong>,
							}}
						/>
					</p>
				),
				reasons: whyAmISeeingThisReasons,
			}}
		/>
	);
};
